/**
 * "Show me" from the page info dialog: bring the control a task describes into
 * view and mark it for a moment, so the reader can find what they just read
 * about. Targets are tagged with `data-page-info-target` (see
 * `pageInfoTarget`); the dialog only offers the button for targets that
 * resolved when it opened, so a missing one here means it unmounted since.
 */
import { PAGE_INFO_TARGET_ATTR } from "./pageInfoTarget";

export const PAGE_INFO_HIGHLIGHT_CLASS = "page-info-highlight";
export const PAGE_INFO_HIGHLIGHT_MS = 2400;

const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const pendingClear = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

export function findPageInfoTarget(targetId: string): HTMLElement | null {
  const matches = document.querySelectorAll<HTMLElement>(
    `[${PAGE_INFO_TARGET_ATTR}="${CSS.escape(targetId)}"]`
  );
  // A target can render twice (a desktop and a compact variant of the same
  // toolbar); the one on screen is the one to point at.
  for (const el of matches) {
    if (isShown(el)) return el;
  }
  return null;
}

function isShown(el: HTMLElement): boolean {
  if (el.closest("[hidden], [inert]")) return false;
  // `checkVisibility` covers display:none / visibility / content-visibility up
  // the tree. jsdom has no layout and no `checkVisibility`, so it counts as shown.
  return typeof el.checkVisibility === "function" ? el.checkVisibility() : true;
}

export function highlightPageInfoTarget(targetId: string): boolean {
  const el = findPageInfoTarget(targetId);
  if (!el) return false;

  el.scrollIntoView({ block: "center", inline: "nearest" });
  if (el.matches(FOCUSABLE)) el.focus({ preventScroll: true });

  const previous = pendingClear.get(el);
  if (previous) clearTimeout(previous);
  // Re-adding the class restarts the pulse when the same target is shown twice.
  el.classList.remove(PAGE_INFO_HIGHLIGHT_CLASS);
  void el.offsetWidth;
  el.classList.add(PAGE_INFO_HIGHLIGHT_CLASS);
  pendingClear.set(
    el,
    setTimeout(() => {
      el.classList.remove(PAGE_INFO_HIGHLIGHT_CLASS);
      pendingClear.delete(el);
    }, PAGE_INFO_HIGHLIGHT_MS)
  );
  return true;
}
